import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomInputComponent } from '../inputs/custom-input/custom-input.component';
import { CustomButtonComponent } from '../inputs/custom-button/custom-button.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '../../services/auth.service';

export type ToastrType = 'success' | 'warning' | 'info' | 'danger' | 'confirm';
export type PresentationType = 'toast' | 'modal';

@Component({
  selector: 'lib-custom-toastr',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CustomInputComponent,
    CustomButtonComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './custom-toastr.component.html',
})
export class CustomToastrComponent implements OnInit {
  @Input() type: ToastrType = 'info';
  @Input() title?: string;
  @Input() message!: string;
  @Input() actionLabel?: string;
  @Input() autoclose = true;
  @Input() timeout = 5000; // 5 seconds by default
  @Input() presentation = 'toast';
  @Input() typeColor = 'negative';
  @Input() requestPassword = false;
  loading = signal<boolean>(false);
  passwordConfirmForm: FormGroup;

  @Output() closeAlert = new EventEmitter<void>();
  @Output() doAction = new EventEmitter<void>();

  private _translate = inject(TranslateService);
  private _timeoutId?: number;
  cancelLabel: string | undefined;

  constructor(
    private _fb: FormBuilder,
    private _authService: AuthService
  ) {
    this.passwordConfirmForm = this._fb.group({
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/),
        ],
      ],
    });
  }

  ngOnInit(): void {
    if (this.autoclose && this.presentation === 'toast') {
      this._timeoutId = window.setTimeout(() => this.onClose(), this.timeout);
    }
  }

  getIconClass() {
    switch (this.type) {
      case 'confirm':
        return '';
      case 'success':
        return 'fa-solid fa-check text-confirm';
      case 'warning':
        return 'fa-solid fa-triangle-exclamation text-warning';
      case 'danger':
        return 'fa-solid fa-circle-exclamation text-error';
      case 'info':
      default:
        return 'fa-solid fa-circle-info text-blue-600';
    }
  }

  getTitleColorClass() {
    switch (this.type) {
      case 'confirm':
        return 'text-error text-center';
      case 'success':
        return 'text-confirm';
      case 'warning':
        return 'text-warning';
      case 'danger':
        return 'text-error';
      case 'info':
      default:
        return 'text-blue-600';
    }
  }

  getContainerClass() {
    if (this.presentation === 'modal') {
      return 'bg-white';
    } else {
      switch (this.type) {
        case 'success':
          return 'bg-confirm_container text-confirm_container_on';
        case 'danger':
          return 'bg-error_container_light text-error_on';
        default:
          return 'bg-white';
      }
    }
  }

  getButtonColorClass() {
    if (this.typeColor === 'positive') {
      return 'bg-confirm_container text-confirm_container_on';
    } else if (this.typeColor === 'negative') {
      return 'bg-error_container_light text-error_on';
    } else {
      return 'bg-white';
    }
  }

  getButtonActionClass() {
    if (this.type === 'confirm') {
      return 'bg-error_container_light hover:bg-error_container_light text-error_on';
    }
    return 'bg-white hover:bg-primary-light text-primary';
  }

  onClose(): void {
    if (this._timeoutId) {
      window.clearTimeout(this._timeoutId);
    }
    this.closeAlert.emit();
  }

  onAction(): void {
    if (this.requestPassword && this.type === 'confirm') {
      if (this.passwordConfirmForm.invalid) {
        this.passwordConfirmForm.markAllAsTouched();
        return;
      }

      this.loading.set(true);
      this._authService.verifyPassword(this.passwordConfirmForm.value.password).subscribe({
        next: (isValid) => {
          this.loading.set(false);
          if (isValid) {
            this._proceedWithAction();
          }
        },
        error: () => {
          this.loading.set(false);
        },
      });
    } else {
      this._proceedWithAction();
    }
  }

  private _proceedWithAction(): void {
    if (this._timeoutId) {
      window.clearTimeout(this._timeoutId);
    }
    this.doAction.emit();
    // Cerrar el toast después de ejecutar la acción
    this.onClose();
  }
}
